import { OrderTypeEnum } from '@/stores/useOrderTypeStore';
import { checkPhoneNumberValidation } from '@/utils/helpers/phoneNumberHelpers';
import { checkProductValidation } from '@/utils/helpers/commonHelpers';
import type { BasicProduct } from '@/types/order';
import type { SmartPasteOrder, SmartPasteWarning } from '@/types/smartPaste';

/**
 * What Smart Paste could not fill in, named field by field.
 *
 * Read off the FORM after the fill, never off the parse envelope. That is the
 * difference between "the parser didn't return a phone" and "the phone box is
 * empty" — only the second is true for the merchant, it stays true when core
 * resolves a field the parse never named, and on Edit it never reports
 * something the merchant had already typed in themselves.
 *
 * The required-field rules mirror the order form's own gate (`sectionFields` in
 * useUpdateOrderPricePreview, plus the Form.Item rules) and reuse its
 * validators, so this panel can't name something submit accepts or stay quiet
 * about something submit rejects.
 *
 * Mirrors `lib/domain/order/smart_paste_gaps.dart` in merchant-new-app — the
 * two clients read the same envelope and should say the same things.
 */

type Translate = (key: string, options?: Record<string, unknown>) => unknown;

/** How loudly to say it. */
export type SmartPasteGapLevel =
  /** The form can't be submitted until the merchant fills this in. */
  | 'missing'
  /** Filled, but worth a second look — an ambiguity, or an optional field we couldn't read. */
  | 'check';

/** One row of the "here's what we didn't catch" list. */
export interface SmartPasteGap {
  /** The field's own label, worded exactly as the form labels it. */
  label: string;
  /** What happened, and what to do about it. */
  hint: string;
  level: SmartPasteGapLevel;
}

/** The subset of form values the gap rules read. */
export interface SmartPasteGapFormValues {
  phoneNumberPrimary?: string;
  countryCodePrimary?: string;
  secondaryPhoneNumber?: string;
  countryCodeSecondary?: string;
  province?: string;
  region_uid?: string;
  address?: string;
  deliveryInstruction?: string;
  orderType?: string;
}

interface BuildGapsArgs {
  /** `form.getFieldsValue()` AFTER the fill has been applied. */
  values: SmartPasteGapFormValues;
  /** The product list the form ends up with — the fill's, or the existing one. */
  products: BasicProduct[];
  order: SmartPasteOrder;
  t: Translate;
}

const blank = (value?: string) => !value || !String(value).trim();

type PhoneInvalidWarning = Extract<SmartPasteWarning, { code: 'PHONE_INVALID' }>;
type MultipleCitiesWarning = Extract<SmartPasteWarning, { code: 'MULTIPLE_CITIES' }>;

/** `PHONE_INVALID` for one of the two phone fields. */
function phoneWarning(order: SmartPasteOrder, secondary: boolean) {
  return (order.warnings ?? []).find(
    (warning): warning is PhoneInvalidWarning =>
      warning.code === 'PHONE_INVALID' && (warning.field === 'phone2') === secondary,
  );
}

function multipleCitiesWarning(order: SmartPasteOrder) {
  return (order.warnings ?? []).find(
    (warning): warning is MultipleCitiesWarning => warning.code === 'MULTIPLE_CITIES',
  );
}

/** Everything the merchant still has to do by hand after a Smart Paste fill. */
export function buildSmartPasteGaps({ values, products, order, t }: BuildGapsArgs): SmartPasteGap[] {
  const gaps: SmartPasteGap[] = [];
  const add = (label: string, hint: string, level: SmartPasteGapLevel = 'missing') =>
    gaps.push({ label: String(t(label)), hint, level });

  // ── Primary phone ────────────────────────────────────────────────────────
  // The field the parser mangles most, and the one an order can't ship without.
  // Core leaves it empty rather than guessing and tells us what it choked on —
  // quoting that back beats "we couldn't read the phone number", because the
  // merchant can see at a glance whether the message or the parser was at fault.
  const badPhone = phoneWarning(order, false);
  if (blank(values.phoneNumberPrimary)) {
    const unreadable = badPhone?.value?.trim();
    add(
      'primary_phone',
      unreadable
        ? String(t('smart_paste_gap_phone_unreadable', { value: unreadable }))
        : String(t('smart_paste_gap_phone_missing')),
    );
  } else if (!checkPhoneNumberValidation(values.phoneNumberPrimary, values.countryCodePrimary)) {
    // Filled, but submit will reject it — same check the form's own gate runs.
    add('primary_phone', String(t('smart_paste_gap_phone_invalid')));
  }

  // ── Destination ──────────────────────────────────────────────────────────
  // The City dropdown gates the Area one, so when both are empty there is only
  // one thing the merchant can act on; naming the area too would be a row they
  // can't use yet.
  if (blank(values.province)) {
    add('city', String(t('smart_paste_gap_province')));
  } else if (blank(values.region_uid)) {
    add('area', String(t('smart_paste_gap_area')));
  } else {
    // Resolved — but if the message named a second city, core picked one of them
    // and the merchant is the only one who knows which is right.
    const cities = multipleCitiesWarning(order);
    if (cities) {
      add(
        'city',
        String(
          t('smart_paste_gap_multiple_cities', {
            used: cities.used ?? '',
            other: cities.value ?? '',
          }),
        ),
        'check',
      );
    }
  }

  // ── Full address ─────────────────────────────────────────────────────────
  // Carries a required asterisk on the form. Submit doesn't enforce it (it is
  // commented out of the CONTACT section), but the panel agrees with what the
  // merchant can see, not with the gate.
  if (blank(values.address)) {
    add('full_address', String(t('smart_paste_gap_address')));
  }

  // ── Products ─────────────────────────────────────────────────────────────
  // Same rule submit applies: a row needs a title, a quantity and a price to
  // count, so a half-filled row is not a filled product list.
  if (!products.some((product) => checkProductValidation(product))) {
    add('products', String(t('smart_paste_gap_products')));
  }

  // ── Delivery instructions ────────────────────────────────────────────────
  // Required for Return/Exchange only. Core promises to fill it for those, so
  // this fires when that promise wasn't kept.
  const needsNote =
    values.orderType === OrderTypeEnum.RETURN || values.orderType === OrderTypeEnum.EXCHANGE;
  if (needsNote && blank(values.deliveryInstruction)) {
    add('delivery_instructions', String(t('smart_paste_gap_instructions')));
  }

  // ── Secondary phone ──────────────────────────────────────────────────────
  // Optional — but unlike the app, this form REJECTS a second number that is
  // present and invalid, so a bad one that landed is a blocker and a bad one
  // core dropped is only worth mentioning.
  const badPhone2 = phoneWarning(order, true);
  if (
    !blank(values.secondaryPhoneNumber) &&
    !checkPhoneNumberValidation(values.secondaryPhoneNumber, values.countryCodeSecondary)
  ) {
    add('secondary_phone', String(t('smart_paste_gap_phone_invalid')));
  } else if (badPhone2 && blank(values.secondaryPhoneNumber)) {
    add(
      'secondary_phone',
      String(t('smart_paste_gap_phone2', { value: badPhone2.value ?? '' })),
      'check',
    );
  }

  return gaps;
}
